import {
  PaletteOptions,
  ColorPartial,
  SimplePaletteColorOptions,
} from '@material-ui/core/styles/createPalette'

export interface ColorsPalette extends PaletteOptions {
  colors?: {
    // TODO: unify with common-ui type
    [key: string]: ColorPartial
    link: ColorPartial
  }
  loading?: SimplePaletteColorOptions
}

export const greyPalette = {
  50: '#fafafb',
  100: '#f5f5f6',
  200: '#eceded',
  300: '#dddedf',
  400: '#c5c7c8',
  500: '#a0a3a5',
  550: '#979797',
  600: '#73777c',
  700: '#5f6469',
  800: '#444a4f',
  900: '#212427',
}

export const greyMultibrandPalette = {
  50: '#f2f2f2',
  100: '#e6e6e6',
  150: '#d9d9d9',
  200: '#cccccc',
  300: '#b3b3b3',
  400: '#999999',
  500: '#7f7f7f',
  600: '#666666',
  700: '#4c4c4c',
  800: '#333333',
  900: '#191919',
}

export const greenPalette = {
  50: '#D7E9C3',
  100: '#D0EABF',
  150: '#C3E0AE',
  200: '#c2eba4',
  250: '#C0DFAB',
  300: '#C8DEB0',
  500: '#5EA610',
  550: '#43aa00',
}

export const bluePalette = {
  800: '#00b8ee',
}

export const redPalette = {
  200: '#f6808e',
}

export const orangePalette = {
  200: '#ffc180',
  300: '#ea9d8e',
}

export const violetPalette = {
  50: '#e9dfeb',
  100: '#C3A7C8',
  300: '#a175f9',
}

export const yellowPalette = {
  200: '#fff4a6',
}

export const textPalette = {
  primary: '#191919',
  secondary: '#4c4c4c',
  hint: '#7f7f7f',
  disabled: 'rgba(25, 25, 25, 0.3)',
}

export const greenPrimary = '#007400'

// MASMOVIL Colors
const masmovilPrimary = {
  main: '#ffe200',
  light: yellowPalette[200],
  dark: '#fdb600',
  disabled: 'rgba(255, 226, 0, 0.25)',
  contrastText: '#000000',
}

const masmovilSecondary = {
  main: '#43aa00',
  light: '#84d749',
  dark: greenPrimary,
  disabled: 'rgba(67, 170, 0, 0.25)',
  contrastText: '#ffffff',
}

const masmovilError = {
  main: '#ee001d',
  light: '#f14a5e',
  dark: '#a70014',
  disabled: 'rgba(238, 0, 29, 0.25)',
  contrastText: '#ffffff',
}

const masmovilWarning = {
  main: '#ff8200',
  light: '#ffb343',
  dark: '#c55300',
  disabled: 'rgba(255, 130, 0, 0.3)',
  contrastText: '#000000',
}

const masmovilSuccess = {
  main: '#43aa00',
  light: '#84d749',
  dark: greenPrimary,
  disabled: 'rgba(67, 170, 0, 0.25)',
  contrastText: '#ffffff',
}

const masmovilInfo = {
  main: '#2365fa',
  light: '#7292ff',
  dark: '#003cc6',
  disabled: 'rgba(35, 101, 250, 0.25)',
  contrastText: '#ffffff',
}

export const masmovilLink = {
  main: '#2365fa',
  light: '#7292ff',
  dark: '#003cc6',
  disabled: 'rgba(35, 101, 250, 0.25)',
  contrastText: '#ffffff',
}

export const masmovilLoading = {
  main: '#666666',
}

export const masmovilPalette = {
  primary: masmovilPrimary,
  secondary: masmovilSecondary,
  link: masmovilLink,
  error: masmovilError,
  warning: masmovilWarning,
  success: masmovilSuccess,
  loading: masmovilLoading,
  info: masmovilInfo,
  text: textPalette,
  grey: greyPalette,
  green: greenPalette,
  blue: bluePalette,
  violet: violetPalette,
  red: redPalette,
  orange: orangePalette,
}

// LLAMAYA Colors
const llamayaPrimary = {
  main: '#6a2375',
  light: '#9c5099',
  dark: '#4c0b57',
  disabled: 'rgba(106, 35, 117, 0.4)',
  contrastText: '#ffffff',
}

const llamayaSecondary = {
  main: '#5ea610',
  light: '#84d749',
  dark: greenPrimary,
  disabled: 'rgba(94, 166, 16, 0.4)',
  contrastText: '#ffffff',
}

const llamayaError = {
  main: '#d53c1d',
  light: '#ff6f49',
  dark: '#9c0000',
  disabled: 'rgba(213, 60, 29, 0.4)',
  contrastText: '#ffffff',
}

const llamayaWarning = {
  main: '#ff8200',
  light: '#ffb343',
  dark: '#c55300',
  disabled: 'rgba(255, 130, 0, 0.4)',
  contrastText: '#000000',
}

const llamayaSuccess = {
  main: '#5ea610',
  light: '#84d749',
  dark: greenPrimary,
  disabled: 'rgba(94, 166, 16, 0.4)',
  contrastText: '#000000',
}

const llamayaInfo = {
  main: '#6a2375',
  light: '#9c5099',
  dark: '#4c0b57',
  disabled: 'rgba(106, 35, 117, 0.4)',
  contrastText: '#ffffff',
}

export const llamayaLink = {
  main: '#8a00a0',
  light: '#be48d3',
  dark: '#580071',
  disabled: 'rgba(153, 0, 176, 0.4)',
  contrastText: '#ffffff',
}

export const llamayaLoading = {
  main: '#666666',
}

export const llamayaAlternative = {
  main: '#ffcc00',
  light: '#ffe479',
  dark: '#ffb600',
  disabled: 'rgba(255, 204, 0, 0.4)',
  contrastText: '#000000',
}

export const llamayaPalette = {
  primary: llamayaPrimary,
  secondary: llamayaSecondary,
  link: llamayaLink,
  error: llamayaError,
  warning: llamayaWarning,
  success: llamayaSuccess,
  loading: llamayaLoading,
  info: llamayaInfo,
  text: textPalette,
  alternative: llamayaAlternative,
  green: greenPalette,
  blue: bluePalette,
  violet: violetPalette,
  red: redPalette,
  orange: orangePalette,
}
